// @features/crud-table/utils/generate-columns.tsx

import type { TableColumn } from "@shared/types/query";
import type { ColumnDef } from "@tanstack/react-table";
import { ExternalLink, FileIcon } from "lucide-react";
import { TableBooleanCell, TableDateCell, TableImageCell } from "./table-cell";

export type RowData = Record<string, unknown>;

export function generateColumns(supabaseColumns: TableColumn[]): ColumnDef<RowData>[] {
	return supabaseColumns
		.filter((col) => col.is_visible)
		.map((col) => {
			const baseColumn: ColumnDef<RowData> = {
				accessorKey: col.column_name,

				header:
					col.column_name.charAt(0).toUpperCase() + col.column_name.slice(1).replace(/_/g, " "),
			};

			if (col.input_type === "file") {
				baseColumn.cell = ({ getValue }) => {
					const fileUrl = getValue() as string;

					if (!fileUrl) return <span className="text-gray-400 text-xs">—</span>;

					const isImage = /\.(jpg|jpeg|png|webp|avif|gif)$/i.test(fileUrl);

					if (isImage) {
						return (
							<img
								src={fileUrl}
								alt="Превью"
								className="w-10 h-10 object-cover rounded-md border"
							/>
						);
					}

					return (
						<a
							href={fileUrl}
							target="_blank"
							rel="noreferrer"
							className="flex items-center gap-1 text-blue-500 hover:underline text-sm"
						>
							<FileIcon className="w-4 h-4" />
							<span>Файл</span>
							<ExternalLink className="w-3 h-3" />
						</a>
					);
				};
			}

			if (col.input_type === "number") {
				baseColumn.meta = { align: "right" };
				baseColumn.cell = ({ getValue }) => {
					const val = getValue();
					return <span className="font-mono">{val !== null ? Number(val) : "—"}</span>;
				};
			}

			if (col.input_type === "date") {
				baseColumn.meta = { align: "right" };
				baseColumn.cell = ({ getValue }) => {
					const val = getValue() as string;
					return <TableDateCell value={val} />;
				};
			}
			if (col.input_type === "image") {
				baseColumn.meta = { align: "right" };
				baseColumn.cell = ({ getValue }) => {
					const val = getValue() as string;
					return <TableImageCell value={val} />;
				};
			}

			if (col.input_type === "boolean") {
				baseColumn.meta = { align: "right" };
				baseColumn.cell = ({ getValue }) => {
					const val = getValue() as boolean;
					return <TableBooleanCell value={val} />;
				};
			}

			if (col.input_type === "badge") {
				baseColumn.meta = { align: "right" };
				baseColumn.cell = ({ getValue }) => {
					const val = getValue() as string;
					return <TableImageCell value={val} />;
				};
			}

			return baseColumn;
		});
}
